import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createDepartment,
  updateDepartment,
  fetchDepartments,
} from "../api/department";
import produce from "immer";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  department: {},
  data: [],
  currentCount: 0,
  totalCount: 0,
  newCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const updateDepartmentAsync = createAsyncThunk(
  "departments/updateDepartmentAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateDepartment(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_department");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_department");
    }
  }
);

export const createDepartmentAsync = createAsyncThunk(
  "departments/createDepartmentAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createDepartment(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_department");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_department");
    }
  }
);

export const getDepartmentsAsync = createAsyncThunk(
  "cases/getDepartmentsAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchDepartments(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_departments");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_departments");
    }
  }
);

const departmentSlice = createSlice({
  name: "departments",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDepartmentsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getDepartmentsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newCount = action.payload.newCount;
      })
      .addCase(getDepartmentsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente avdelingene.");
      })
      .addCase(updateDepartmentAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateDepartmentAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload.id
          );
          draftState.data[index] = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateDepartmentAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke oppdatere avdelingen."
        );
      })
      .addCase(createDepartmentAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createDepartmentAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createDepartmentAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke opprette avdelingen."
        );
      });
  },
});

export const { reducer } = departmentSlice;

export default departmentSlice;
